import React from 'react';
import { motion } from 'framer-motion';
import { ImageIcon, Zap, Users, TrendingUp, Layers, BarChart3, Rocket, Globe, Network } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { useModal } from './ModalContext';
import WhataKitModal from './WhataKitModal';
import EarlyPackModal from './EarlyPackModal';
import WattModal from './WattModal';
import { EXTERNAL_LINKS } from '../config';
import './Ecosystem.css';

const Ecosystem = () => {
  const { t, language } = useLanguage();
  const isEN = language === 'en';
  const { isModalOpen, closeModal, openWhataKit, openEarlyPack, openWatt } = useModal();
  // Ecosystem items with translation keys
  const ecosystemItems = [
    {
      icon: <ImageIcon size={32} />,
      titleKey: "tradersTitle",
      descriptionKey: "tradersDesc",
      featuresKeys: ["tradersFeature1", "tradersFeature2", "tradersFeature3"],
      statusKey: "statusReady",
      statusInternal: "Ready",
      color: "var(--color-brand)",
      ctaKey: "tradersCta",
      link: EXTERNAL_LINKS.TOOLS_ORDERBOOK
    },
    {
      icon: <TrendingUp size={32} />,
      titleKey: "fansTitle",
      descriptionKey: "fansDesc",
      featuresKeys: ["fansFeature1", "fansFeature2", "fansFeature3"],
      statusKey: "statusReady",
      statusInternal: "Ready",
      color: "var(--color-brand)",
      ctaKey: "fansCta",
      link: EXTERNAL_LINKS.TOOLS_PLATFORM
    },
    {
      icon: <Zap size={32} />,
      titleKey: "creatorsTitle",
      descriptionKey: "creatorsDesc",
      featuresKeys: ["creatorsFeature1", "creatorsFeature2", "creatorsFeature3"],
      statusKey: "statusReady",
      statusInternal: "Ready",
      color: "var(--color-brand)",
      ctaKey: "creatorsCta",
      link: EXTERNAL_LINKS.TOOLS_PLATFORM
    },
    {
      icon: <Users size={32} />,
      titleKey: "enterpriseTitle",
      descriptionKey: "enterpriseDesc",
      featuresKeys: ["enterpriseFeature1", "enterpriseFeature2", "enterpriseFeature3"],
      statusKey: "statusUpcoming",
      statusInternal: "Upcoming",
      color: "var(--color-brand)",
      ctaKey: "enterpriseCta",
      link: null
    }
  ];

  // Benefits with translation keys
  const benefits = [
    {
      icon: <Layers size={24} />,
      titleKey: "benefitPlatformTitle",
      descriptionKey: "benefitPlatformDesc"
    },
    {
      icon: <BarChart3 size={24} />,
      titleKey: "benefitInsightsTitle",
      descriptionKey: "benefitInsightsDesc"
    },
    {
      icon: <Rocket size={24} />,
      titleKey: "benefitToolsTitle",
      descriptionKey: "benefitToolsDesc"
    }
  ];

  return (
    <section className="ecosystem section" id="ecosystem">
      <div className="container">
        <motion.div
          className="bf-header eco-header"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35 }}
        >
          <div className="bf-eyebrow">
            <span className="bf-eyebrow-dot" />
            {isEN ? 'the ecosystem' : 'el ecosistema'}
          </div>
          <div className="eco-header-row">
            <h2 className="bf-title">
              {isEN
                ? <><span className="bf-title-accent">The WhataLab</span><br />Universe.</>
                : <><span className="bf-title-accent">El Universo</span><br />WhataLab.</>
              }
            </h2>
            <p className="eco-subtitle">{t('ecosystem.subtitle')}</p>
          </div>
        </motion.div>

        {/* Infinite Auto-scroll Carousel */}
        <div className="eco-carousel-wrapper">
          {/* Fade edges */}
          <div className="eco-carousel-fade eco-carousel-fade--left" />
          <div className="eco-carousel-fade eco-carousel-fade--right" />

          <div className="eco-carousel-track">
            {[...ecosystemItems, ...ecosystemItems, ...ecosystemItems].map((item, i) => (
              <div key={i} className="eco-card">
                <div className="eco-card-top">
                  <div className="eco-card-icon-wrap" style={{ borderColor: item.color }}>
                    {item.icon}
                  </div>
                  <span className={`eco-card-status ${item.statusInternal === 'Ready' ? 'eco-card-status--ready' : 'eco-card-status--upcoming'}`}>
                    {t(`ecosystem.${item.statusKey}`)}
                  </span>
                </div>

                <h3 className="eco-card-title">{t(`ecosystem.${item.titleKey}`)}</h3>
                <p className="eco-card-desc">{t(`ecosystem.${item.descriptionKey}`)}</p>

                <ul className="eco-card-features">
                  {item.featuresKeys.map((fk, fi) => (
                    <li key={fi} className="eco-card-feature">
                      <span className="eco-card-feature-dot" />
                      {t(`ecosystem.${fk}`)}
                    </li>
                  ))}
                </ul>

                {item.link ? (
                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="eco-card-cta">
                    {t(`ecosystem.${item.ctaKey}`)}
                    <span className="eco-card-cta-arrow">→</span>
                  </a>
                ) : (
                  <span className="eco-card-cta eco-card-cta--disabled">
                    {t(`ecosystem.${item.ctaKey}`)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WhataKit Purchase Modal */}
      <WhataKitModal 
        isOpen={isModalOpen('whatakit')} 
        onClose={closeModal} 
      />

      {/* Early Pack Purchase Modal */}
      <EarlyPackModal 
        isOpen={isModalOpen('earlypack')} 
        onClose={closeModal} 
      />

      {/* WATT Purchase Modal */}
      <WattModal 
        isOpen={isModalOpen('watt')} 
        onClose={closeModal} 
      />
    </section>
  );
};

export default Ecosystem;
